import {
  EnvironmentInjector,
  inject,
  provideAppInitializer,
  runInInjectionContext,
} from '@angular/core';
import Keycloak from 'keycloak-js';
import {
  AutoRefreshTokenService,
  createInterceptorCondition,
  INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  IncludeBearerTokenCondition,
  provideKeycloak,
  UserActivityService,
  withAutoRefreshToken,
} from 'keycloak-angular';
import { environment } from '../environments/environment';

// Automatically refresh the token while the user is active. keycloak-js fires
// an OnTokenExpired event as the access token nears expiry; withAutoRefreshToken
// reacts by calling keycloak.updateToken() when the user has been active within
// sessionTimeout, otherwise it runs onInactivityTimeout.
const autoRefreshTokenFeature = withAutoRefreshToken({
  onInactivityTimeout: 'logout',
  // Realm SSO Session Idle / refresh token lifetime is 1800s (30 min).
  // Kept just under it so the idle logout fires before the refresh token dies.
  sessionTimeout: 1_680_000, // 28 min idle window before logout
});

export const appKeycloakProvider = provideKeycloak({
  config: {
    url: environment.keycloakUrl,
    realm: environment.keycloakRealm,
    clientId: environment.keycloakClientId,
  },
  // NOTE: initOptions / features are intentionally omitted here. keycloak-angular's
  // built-in initializer swallows init errors (it only console.errors them), which
  // let the CMS boot with no session when the auth server was down. We init
  // manually below (appKeycloakInitializer) so a failure aborts bootstrap instead.
  // Required by withAutoRefreshToken.
  providers: [AutoRefreshTokenService, UserActivityService],
});

/**
 * Thrown when Keycloak fails to initialize or does not authenticate the user.
 * main.ts uses this to distinguish an auth failure (→ maintenance page) from
 * unrelated bootstrap errors.
 */
export class KeycloakInitError extends Error {}

// Fail faster than the ~60s OS network timeout so the maintenance page appears sooner.
const INIT_TIMEOUT_MS = 30_000;

/**
 * Initializes Keycloak with onLoad: 'login-required' and re-throws on failure so
 * that a down auth server aborts app bootstrap instead of silently loading the CMS.
 * Mirrors keycloak-angular's internal initializer (feature.configure() before init)
 * but propagates errors and enforces a timeout.
 */
export const appKeycloakInitializer = provideAppInitializer(async () => {
  const keycloak = inject(Keycloak);
  const injector = inject(EnvironmentInjector);
  runInInjectionContext(injector, () => autoRefreshTokenFeature.configure());

  const timeout = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new KeycloakInitError('Keycloak init timed out')), INIT_TIMEOUT_MS),
  );

  try {
    const authenticated = await Promise.race([
      keycloak.init({ onLoad: 'login-required' }),
      timeout,
    ]);
    if (!authenticated) {
      throw new KeycloakInitError('Keycloak did not authenticate the user');
    }
  } catch (error) {
    throw error instanceof KeycloakInitError
      ? error
      : new KeycloakInitError('Keycloak initialization failed', { cause: error });
  }
});

/**
 * Builds a RegExp that matches requests starting with any of the given URLs.
 */
function buildUrlPattern(urls: string[]): RegExp {
  const escaped = urls.map(url =>
    url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // escape regex metacharacters
  );

  // join into an "OR" group and allow any path after it
  const pattern = `^(${escaped.join('|')})(/.*)?$`;
  return new RegExp(pattern, 'i');
}

const urlCondition: IncludeBearerTokenCondition = createInterceptorCondition<IncludeBearerTokenCondition>({
  urlPattern: buildUrlPattern([
    environment.apiUrl,
  ]),
});

export const AppKeycloakBearerTokenInterceptorConfig = {
  provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  useValue: [
    urlCondition, // <-- Note that multiple conditions might be added.
  ]
}
